/**
 * Q1a — N3 · Competência 2 (Algébrico-funcional) · task-model H3.11.K01
 *
 *   "Dados dois vértices opostos de um retângulo ou quadrado no plano cartesiano,
 *    MARCAR na malha os dois vértices restantes."
 *
 * ADAPTADO DA QUESTÃO 01 do material no GeoGebra. A habilidade foi desdobrada em três
 * formatos de comando, cada um com o seu código, porque cada formato pede uma coisa
 * diferente do aluno:
 *
 *  - H3.11.K01 (este, N3): marcar os vértices na malha — age-se sobre a figura;
 *  - H3.11.K02 (N1): escolher o par de coordenadas entre cinco alternativas;
 *  - H3.11.K03 (N1): completar a ficha com as coordenadas, como no GeoGebra.
 *
 * Os três compartilham os DOIS NÍVEIS e os erros típicos de `_comum/quadrado.ts`:
 * retângulo de lados paralelos aos eixos (ler e combinar coordenadas) e quadrado
 * inclinado com vértices na malha (ponto médio e giro da meia diagonal).
 *
 * O DIAGNÓSTICO olha primeiro para os erros típicos exatos — coordenadas invertidas, o
 * método do retângulo aplicado ao quadrado — e, se nenhum casar, para o quadrilátero
 * que os quatro pontos formam.
 */

import type { Rng } from '../../core/rng.ts';
import { artigo, nome, numeroDe } from '../../core/pt.ts';
import { sampleWhere } from '../../core/sampling.ts';
import type { ConstructionSnapshot, GeoId, GeoObject } from '../../geom/construction.ts';
import { EMPTY_SNAPSHOT } from '../../geom/construction.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  QUAD_DIAGONAL_COMO_LADO,
  QUAD_LOSANGO,
  QUAD_NAO_QUADRADO,
  QUAD_QUANTIDADE,
  QUAD_RETANGULO,
  RET_ERRADO,
  RET_FEZ_QUADRADO,
} from '../../schema/errors.ts';
import type { ConstructionTarget, DiagnosticPredicate, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type Inline } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import {
  aneisDeRevisao,
  classificarQuadrilatero,
  LEGENDA_ANEIS,
  listaTxt,
  mesmoConjunto,
  mesmoPonto,
  pontosDoAluno,
  pontoTxt,
  type TipoQuadrilatero,
} from '../_comum/plano.ts';
import {
  acertou,
  cenarioDoPlano,
  CONTEXTOS_QUADRILATERO,
  descricaoDoPlano,
  errosDoQuadrilatero,
  figuraDoPlano,
  quadroDoPlano,
  relacaoDosDados,
  resolucaoDoQuadrilatero,
  sortearQuadrilatero,
  U_PLANO,
  type NivelQuadrilatero,
  type Quadrilatero,
} from '../_comum/quadrado.ts';

/** Unidades de desenho por unidade do plano. */
export const U = U_PLANO;

export interface Params {
  readonly nivel: NivelQuadrilatero;
  readonly contexto: number;
  readonly q: Quadrilatero;
}

/** Sorteio comum aos três formatos: nível meio a meio, contexto e a figura. */
export function sortearParams(rng: Rng): Params {
  const nivel: NivelQuadrilatero = rng.bool() ? 'retangulo' : 'inclinado';
  const contexto = rng.int(0, CONTEXTOS_QUADRILATERO.length - 1);
  const q = sampleWhere(
    rng.fork('geometria'),
    (g) => sortearQuadrilatero(g, nivel),
    (c) => c !== null,
    {
      label: 'quadrilatero',
      maxTries: 200,
    },
  ) as Quadrilatero;
  return { nivel, contexto, q };
}

const DESCRICAO: Readonly<Record<TipoQuadrilatero, string>> = {
  quadrado: 'um quadrado',
  retangulo: 'um retângulo que não é quadrado',
  losango: 'um losango que não é quadrado',
  paralelogramo: 'um paralelogramo que não é retângulo',
  outro: 'uma figura que não é paralelogramo',
};

function alvoDe(p: Params): ConstructionTarget {
  const { q } = p;
  const erros = errosDoQuadrilatero(q);
  const figura = q.nivel === 'retangulo' ? 'retângulo' : 'quadrado';
  const doAluno = (objs: ReadonlyMap<GeoId, GeoObject>): Vec2[] => pontosDoAluno(objs, U);
  const formado = (ms: readonly Vec2[]) => classificarQuadrilatero([q.A, q.C, ...ms]);
  const dadosNaDiagonal = (f: ReturnType<typeof classificarQuadrilatero>): boolean =>
    f.diagonais !== null && f.diagonais[0] === 0 && f.diagonais[1] === 1;
  const ok = (ms: readonly Vec2[]): boolean => ms.length === 2 && acertou(q, ms);

  const geometricos: DiagnosticPredicate[] =
    q.nivel === 'retangulo'
      ? [
          {
            error: RET_FEZ_QUADRADO,
            test: (objs) => {
              const f = formado(doAluno(objs));
              return f.tipo === 'quadrado' && dadosNaDiagonal(f);
            },
          },
          { error: RET_ERRADO, test: () => true },
        ]
      : [
          {
            error: QUAD_DIAGONAL_COMO_LADO,
            test: (objs) => {
              const f = formado(doAluno(objs));
              return f.tipo === 'quadrado' && !dadosNaDiagonal(f);
            },
          },
          { error: QUAD_RETANGULO, test: (objs) => formado(doAluno(objs)).tipo === 'retangulo' },
          { error: QUAD_LOSANGO, test: (objs) => formado(doAluno(objs)).tipo === 'losango' },
          { error: QUAD_NAO_QUADRADO, test: () => true },
        ];

  return {
    describe: [txt(`Marque os vértices B e D que completam o ${figura}.`)],

    predicate(objs) {
      const certo = ok(doAluno(objs));
      return {
        name: 'completaQuadrilatero',
        ok: certo,
        residual: certo ? 0 : 1,
        unit: 'count',
        tolerance: 0,
        message: [
          txt(
            certo
              ? `Os quatro pontos formam o ${figura} pedido.`
              : `Os quatro pontos ainda não formam o ${figura} pedido.`,
          ),
        ],
      };
    },

    diagnostics: [
      { error: QUAD_QUANTIDADE, test: (objs) => doAluno(objs).length !== 2 },
      // Os erros típicos, pelo conjunto exato de pontos que produzem.
      ...erros.map((e): DiagnosticPredicate => ({
        error: e.error,
        test: (objs) => mesmoConjunto(doAluno(objs), e.pontos),
      })),
      ...geometricos,
    ],

    robustnessTrials: 0,

    analyze(objs) {
      const ms = doAluno(objs);
      const out: Inline[] = [];
      out.push(
        txt(
          ms.length === 0
            ? 'Você não marcou nenhum ponto.'
            : `Você marcou ${artigo('m', numeroDe(ms.length))} ${nome('ponto', numeroDe(ms.length))} ${listaTxt(ms)}.`,
        ),
      );
      if (ms.length === 2) {
        const tipico = erros.find((e) => mesmoConjunto(ms, e.pontos));
        if (tipico) {
          out.push(br, ...tipico.explicacao);
        } else if (!ok(ms)) {
          out.push(br, txt(`Com A e C, os quatro pontos formam ${DESCRICAO[formado(ms).tipo]}.`));
        }
      }
      if (!ok(ms)) {
        out.push(
          br,
          strong('Os vértices que faltavam: '),
          txt(`B = ${pontoTxt(q.B)} e D = ${pontoTxt(q.D)}.`),
        );
      }
      return out;
    },

    reviewShapes(objs) {
      const ms = doAluno(objs);
      const alvo = [q.B, q.D];
      return [
        {
          t: 'poly',
          pts: [q.A, q.B, q.C, q.D].map((v) => ({ x: v.x * U, y: v.y * U })),
          closed: true,
          role: 'construction',
        },
        ...aneisDeRevisao(
          ms.filter((m) => alvo.some((r) => mesmoPonto(m, r))),
          ms.filter((m) => !alvo.some((r) => mesmoPonto(m, r))),
          alvo.filter((r) => !ms.some((m) => mesmoPonto(m, r))),
          U,
        ),
      ];
    },

    reviewLegend: [...LEGENDA_ANEIS, txt(` O contorno tracejado é o ${figura} esperado.`)],
  };
}

export const verticesDoQuadrado: ItemTemplate<Params, ConstructionSnapshot> = {
  id: templateId('h3-11-K01-quadrado'),
  // v2: dois níveis — retângulo alinhado aos eixos e quadrado inclinado.
  // v3: eixos numerados só na origem e nos extremos.
  version: 3,
  taskModel: taskModelCode('H3.11.K01'),
  skills: [skillCode('H3.11')],
  axis: 'II',
  level: 'N3',
  title: 'Quadrilátero no plano: marcar os vértices',
  responseModel: '2PL',

  difficultySources: ['steps.2'],

  params: sortearParams,
  solve: () => EMPTY_SNAPSHOT,
  buggy: [],

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.nivel === 'inclinado') fontes.push('steps.3plus', 'non-round-numbers');
    return fontes;
  },

  build(p) {
    const ctx = CONTEXTOS_QUADRILATERO[p.contexto] as (typeof CONTEXTOS_QUADRILATERO)[number];
    const rel = relacaoDosDados(p.nivel);
    const figura = p.nivel === 'retangulo' ? 'retângulo' : 'quadrado';
    const desc = `${descricaoDoPlano(p.q)} É possível marcar pontos nos cruzamentos da malha.`;

    return {
      stem: [txt(`${ctx.abertura(p.nivel)} ${rel.antes}`), strong(rel.destaque), txt(rel.depois)],
      comando: [
        txt('Marque, na malha, os vértices '),
        strong('B e D'),
        txt(` que completam o ${figura}.`),
      ],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'plano',
          scene: figuraDoPlano(p.q),
          alt: desc,
        },
      ],
      interaction: {
        kind: 'construct',
        toolbelt: ['select', 'point', 'delete', 'undo'],
        givens: EMPTY_SNAPSHOT,
        backdrop: cenarioDoPlano(p.q),
        viewBox: quadroDoPlano(),
        perturbable: [],
        target: alvoDe(p),
        snapping: {
          pixelRadius: 18,
          lattice: { on: true, step: U },
          toPoints: true,
          toIntersections: false,
          toObjects: false,
        },
        maxActions: 40,
        // Sem letras nos pontos marcados: a prancheta chamaria o primeiro de "A".
        labelCreatedPoints: false,
      },
      rationale: [strong('Resolução. '), ...resolucaoDoQuadrilatero(p.q)],
      declared: p.nivel === 'retangulo' ? 'facil' : 'dificil',
    };
  },
};
